import React, { useRef, useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaUserSlash, FaUserCheck, FaCopy, FaSearch, FaEye } from 'react-icons/fa';
import AddEditPopup from './AddEditPopup';
import { formatDate } from '../../utils/Utils';
import { BeatLoader } from "react-spinners";
import { useFetchUsers, useDeleteUser } from '../../hooks/useUsers';
import { useFetchOrganizations } from '../../hooks/useOrganizations';
import { toast } from "react-toastify";
import Swal from 'sweetalert2';
import axiosInstance from '../../lib/axios';
import { useQueryClient } from '@tanstack/react-query';


function UserManagementTable({ search, click, lastLogin, userType, enrollmentDate, organization, plan, planType, statusType }) {
  const [showPopup, setShowPopup] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const queryClient = useQueryClient();
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [selectedOrganizationName, setSelectedOrganizationName] = useState("");
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const organizationNameRef = useRef("");
  const orgDropdownRef = useRef(null);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const getReadableStatus = (status) => {
    if (!status) return "";
    switch (status) {
      case "active":
        return "Active";
      case "confirm_request_for_custom_plan":
        return "Awaiting Plan Confirmation";
      case "uncompleted_user_details":
        return "Profile Incomplete";
      case "inactive":
        return "Inactive";
      case "pending":
        return "Pending Approval";
      default:
        // fallback: prettify with spaces & capitalization
        return status
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase());
    }
  };


  const TruncateCell = ({ value, width = "max-w-[180px]" }) => {
    if (!value) return null;

    return (
      <div className={`relative group ${width}`}>
        <div className="truncate text-left cursor-default">
          {value}
        </div>
        <div className="
            absolute left-[-10px] bottom-full mt-1
            hidden group-hover:block
            bg-gray-900 text-white text-xs
            py-2 rounded-md shadow-lg
            whitespace-normal break-words
            z-50 min-w-[200px] max-w-[400px] px-3
          ">
          {value}
        </div>
      </div>
    );
  };

  const { data, error, isLoading } = useFetchUsers(currentPage, search, lastLogin, userType, enrollmentDate, organization, plan, planType, statusType);
  const deleteUserMutation = useDeleteUser();

  const {
    data: orgsData,
    isLoading: orgsLoading,
  } = useFetchOrganizations(
    0,
    orgSearchTerm === selectedOrganizationName ? "" : orgSearchTerm,
    undefined
  );

  const filteredOrganizations = orgsData?.organizations || [];

  const handleEdit = (id) => {
    const customer = data.users.find(c => c.id === id);
    setSelectedCustomer(customer);
    setShowPopup(true);
  };

  const handleDelete = (id) => {
    deleteUserMutation.mutate(id);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const updateUserStatusInCache = (userId, newStatus) => {
    queryClient.setQueryData(
      ['users', { currentPage, search, lastLogin, userType, enrollmentDate, organization, plan, planType, statusType }],
      (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          users: oldData.users.map((u) =>
            u.id === userId ? { ...u, status: newStatus } : u
          ),
        };
      }
    );
  };

  const revokeUser = async (id, name) => {
    const result = await Swal.fire({
      title: 'Disable User?',
      text: `Are you sure you want to disable access for ${name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, disable',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    try {
      await axiosInstance.put(`/admin/user/revoke/${id}`);
      toast.success("User disabled successfully!");
      updateUserStatusInCache(id, "disabled");
    } catch (error) {
      toast.error("Failed to disable user");
    }
  };

  const activateUser = async (id, name) => {
    const result = await Swal.fire({
      title: 'Activate User?',
      text: `Are you sure you want to activate ${name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#22c55e',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, activate',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    try {
      await axiosInstance.put(`/admin/user/active/${id}`);
      toast.success("User activated successfully!");
      updateUserStatusInCache(id, "active");
    } catch (error) {
      toast.error("Failed to activate user");
    }
  };

  const openDuplicateModal = (customerId) => {
    setSelectedCustomerId(customerId);
    setSelectedOrganizationId("");
    setSelectedOrganizationName("");
    setOrgSearchTerm("");
    setShowOrgDropdown(false);
    organizationNameRef.current = "";
    setShowDuplicateModal(true);
  };

  const handleSelectOrganization = (org) => {
    setSelectedOrganizationId(org.id);
    setSelectedOrganizationName(org.name);
    setOrgSearchTerm(org.name);
    setShowOrgDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target)) {
        setShowOrgDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDuplicate = async () => {
    if (!selectedOrganizationId) {
      toast.error("Please select an organization");
      return;
    }

    const name = organizationNameRef.current.trim();
    if (!name) {
      toast.error("Please enter the new organization name");
      return;
    }

    setIsDuplicating(true);
    try {
      await axiosInstance.put(
        `/admin/organization/duplicate/${selectedOrganizationId}/${selectedCustomerId}`,
        { name }
      );
      toast.success("Organization duplicated successfully");
      setShowDuplicateModal(false);
      setSelectedCustomerId(null);
      setSelectedOrganizationId("");
      setSelectedOrganizationName("");
      setOrgSearchTerm("");
      queryClient.invalidateQueries(['users']);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to duplicate organization"
      );
    } finally {
      setIsDuplicating(false);
    }
  };

  const deleteOrganization = async (idUser, name) => {
    const result = await Swal.fire({
      title: 'Delete Sandbox Organization?',
      text: `This will delete the duplicated (sandbox) organization for ${name}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          await axiosInstance.put(`/admin/organization/delete/${idUser}`);
          return true;
        } catch (error) {
          Swal.showValidationMessage(
            error?.response?.data?.message || "Failed to delete organization"
          );
          return false;
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });

    if (result.isConfirmed) {
      toast.success("Sandbox organization deleted successfully");
      queryClient.invalidateQueries(['users']);
    }
  };

  const showDuplicatedPassword = (password) => {
    Swal.fire({
      title: 'Duplicated Account Password',
      html: `
      <div style="display:flex; align-items:center; gap:8px; justify-content:center;">
        <code style="background:#f3f4f6; padding:6px 10px; border-radius:6px; font-size:14px;">
          ${password}
        </code>
      </div>
    `,
      icon: 'info',
      confirmButtonText: 'Copy',
      showCancelButton: true,
      cancelButtonText: 'Close',
      confirmButtonColor: '#3b82f6',
    }).then((result) => {
      if (result.isConfirmed) {
        navigator.clipboard.writeText(password);
        toast.success('Password copied to clipboard');
      }
    });
  };

  if (isLoading) return <div className='col-span-full flex justify-center items-center'><BeatLoader /></div>;
  if (error) return <div className='col-span-full flex justify-center items-center'>Error loading users.</div>;

  return (
    <div className="col-span-full bg-white shadow-lg rounded-sm border border-slate-200">
      <header className="px-5 py-4 border-b border-slate-100">
        <h2 className="font-semibold text-slate-800">Users</h2>
      </header>
      <div className="p-3">
        <div className="overflow-x-auto">
          <table className="table-auto w-full">
            <thead className="text-xs font-semibold uppercase text-slate-400 bg-slate-50">
              <tr>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-left">First Name</div></th>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-left">Last Name</div></th>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-left">Email</div></th>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-left">Organization</div></th>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-left">Industry</div></th>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-left">Country</div></th>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-left">Plan</div></th>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-left">Status</div></th>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-left">Last Login</div></th>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-left">Enrollment Date</div></th>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-center">Action</div></th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-slate-100">
              {data?.users?.map(customer => {
                const isErrorStatus = [
                  "confirm_request_for_custom_plan",
                  "uncompleted_user_details",
                ].includes(customer.status);
                const canManageOrganization = ["ROLE_QA", "ORG_OWNER"].includes(customer.role);

                return (
                  <tr
                    key={customer.id}
                    className={isErrorStatus ? "bg-red-100 text-red-700" : ""}
                  >
                    <td className="p-2"><TruncateCell value={customer.firstName} width="max-w-36" /></td>
                    <td className="p-2"><TruncateCell value={customer.lastName} width="max-w-36" /></td>
                    <td className="p-2"><TruncateCell value={customer.email} width="max-w-24" /></td>
                    <td className="p-2"><TruncateCell value={customer.organization} width="max-w-24" /></td>
                    <td className="p-2"><TruncateCell value={customer.industry} width="max-w-24" /></td>
                    <td className="p-2"><TruncateCell value={customer.country} width="max-w-32" /></td>
                    <td className="p-2"><TruncateCell value={customer.planName} width="max-w-12" /></td>
                    <td className="p-2"><TruncateCell value={getReadableStatus(customer?.status)} width="max-w-20" /></td>
                    <td className="p-2">
                      <TruncateCell
                        value={!customer.lastLogin ? "" : formatDate(customer.lastLogin)}
                        width="max-w-24"
                      />
                    </td>
                    <td className="p-2">
                      <TruncateCell
                        value={!customer.enrollmentDate ? "" : formatDate(customer.enrollmentDate)}
                        width="max-w-24"
                      />
                    </td>
                    <td className="p-2 text-center">
                      {customer.status === "disabled" ? (
                        <button
                          type="button"
                          onClick={() => activateUser(customer?.id, `${customer?.firstName} ${customer?.lastName}`)}
                          className="inline-flex items-center justify-center text-green-500 hover:text-green-700 hover:bg-green-50 rounded p-1 transition-colors duration-150"
                          title="Activate User"
                        >
                          <FaUserCheck size={18} />
                        </button>
                      ) : customer.status === "active" ? (
                        <button
                          type="button"
                          onClick={() => revokeUser(customer?.id, `${customer?.firstName} ${customer?.lastName}`)}
                          className="inline-flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1 transition-colors duration-150"
                          title="Disable User"
                        >
                          <FaUserSlash size={18} />
                        </button>
                      ) : null}

                      {canManageOrganization && (customer.hasSandboxOrg ? (
                        <button
                          type="button"
                          onClick={() => deleteOrganization(customer?.id, `${customer?.firstName} ${customer?.lastName}`)}
                          className="inline-flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1 transition-colors duration-150 ml-2"
                          title="Delete Sandbox Organization"
                        >
                          <FaTrash size={16} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openDuplicateModal(customer.id)}
                          className="inline-flex items-center justify-center text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded p-1 transition-colors duration-150 ml-2"
                          title="Duplicate Organization"
                        >
                          <FaCopy size={18} />
                        </button>
                      ))}

                      {customer.isDuplicated && customer.duplicatedAccountPassword && (
                        <button
                          type="button"
                          onClick={() => showDuplicatedPassword(customer.duplicatedAccountPassword)}
                          className="inline-flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded p-1 transition-colors duration-150 ml-2"
                          title="View Duplicated Account Password"
                        >
                          <FaEye size={18} />
                        </button>
                      )}

                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="flex justify-center mt-4">
          <button
            className="px-3 min-w-[100px] py-1 bg-gray-200 rounded-l-lg hover:bg-gray-300"
            disabled={currentPage === 0}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Previous
          </button>
          <span className="px-3 py-1">Page {currentPage + 1} of {data?.totalPages}</span>
          <button
            className="px-3 min-w-[100px] py-1 bg-gray-200 rounded-r-lg hover:bg-gray-300"
            disabled={currentPage === data?.totalPages - 1}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Next
          </button>
        </div>

        {showPopup && (
          <AddEditPopup customer={selectedCustomer} onClose={() => setShowPopup(false)} />
        )}
      </div>
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-[450px] p-6">
            <h2 className="text-lg font-semibold mb-4">
              Duplicate Organization
            </h2>

            <div className="mb-4" ref={orgDropdownRef}>
              <label className="block text-sm font-medium mb-2">
                Target Organization
              </label>

              <div className="relative">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="text"
                    value={orgSearchTerm}
                    onChange={(e) => {
                      setOrgSearchTerm(e.target.value);
                      setShowOrgDropdown(true);
                      if (e.target.value !== selectedOrganizationName) {
                        setSelectedOrganizationId("");
                      }
                    }}
                    onFocus={() => setShowOrgDropdown(true)}
                    placeholder="Search for an organization..."
                    className="w-full border rounded pl-9 pr-3 py-2"
                  />
                </div>

                {showOrgDropdown && (
                  <div className="absolute z-10 mt-1 w-full max-h-52 overflow-y-auto bg-white border rounded shadow-lg">
                    {orgsLoading ? (
                      <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                    ) : filteredOrganizations.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">No organizations found</div>
                    ) : (
                      filteredOrganizations.map((org) => (
                        <div
                          key={org.id}
                          onClick={() => handleSelectOrganization(org)}
                          className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 ${org.id === selectedOrganizationId ? "bg-blue-100 font-medium" : ""
                            }`}
                        >
                          {org.name}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Enter new organization name"
                onChange={(e) => {
                  organizationNameRef.current = e.target.value;
                }}
                className="w-full border rounded px-3 py-2"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDuplicateModal(false)}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleDuplicate}
                disabled={isDuplicating}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDuplicating ? "Duplicating..." : "Duplicate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagementTable;
